'use client'

import { motion, useInView } from 'framer-motion'
import { Award, Users, Target, Zap, ShieldCheck, BrainCircuit, Handshake, ArrowRight, Rocket, Globe, BarChart3 } from 'lucide-react'
import { useRef, useEffect, useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const CountingNumber = ({ value, duration = 2, suffix = '' }: { value: string, duration?: number, suffix?: string }) => {
  const [displayValue, setDisplayValue] = useState("0")
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  
  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const end = parseInt(value.replace(/[^0-9.-]+/g,""));
    const prefix = value.replace(/[0-9.-]+/g, "");
    
    if (start === end) return;

    const steps = 50;
    const increment = end / steps;
    const incrementTime = (duration * 1000) / steps;
    
    let timer = setInterval(() => {
      start = Math.min(start + increment, end);
      setDisplayValue(prefix + Math.floor(start).toLocaleString() + suffix)
      if (start === end) clearInterval(timer)
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration, suffix, isInView]);

  return <span ref={ref}>{displayValue}</span>;
};

const stats = [
  { number: '1M', suffix: '+', label: 'Opportunities Indexed', description: 'Federal, State, Local & Grants' },
  { number: '50', suffix: 'x', label: 'Faster Insights', description: 'AI-driven analysis speed' },
  { number: '90', suffix: '+', label: 'User Satisfaction', description: 'Based on client feedback' },
  { number: '99.9', suffix: '%', label: 'Platform Uptime', description: 'Reliable access guaranteed' },
]

const values = [
  {
    icon: ShieldCheck,
    title: 'Integrity First',
    description: 'We operate with unwavering ethical standards, building trust through transparency and accountability.',
    gradient: 'from-blue-500 to-cyan-500',
    color: '#0ea5e9',
  },
  {
    icon: BrainCircuit,
    title: 'Drive Innovation',
    description: 'We relentlessly pursue cutting-edge solutions, leveraging AI to solve complex contracting challenges.',
    gradient: 'from-violet-500 to-purple-500',
    color: '#8b5cf6',
  },
  {
    icon: Users,
    title: 'Customer Obsessed',
    description: 'Our clients\' success is our success. We listen intently and deliver exceptional value and support.',
    gradient: 'from-emerald-500 to-teal-500',
    color: '#10b981',
  },
  {
    icon: Handshake,
    title: 'Stronger Together',
    description: 'We foster collaboration, believing diverse perspectives and teamwork lead to the best outcomes.',
    gradient: 'from-amber-500 to-orange-500',
    color: '#f59e0b',
  },
  {
    icon: Target,
    title: 'Deliver Impact',
    description: 'We focus on tangible results, empowering clients to compete, win, and thrive in government markets.',
    gradient: 'from-rose-500 to-pink-500',
    color: '#f43f5e',
  },
  {
    icon: Award,
    title: 'Strive for Excellence',
    description: 'We are committed to the highest quality standards, constantly learning and improving our platform.',
    gradient: 'from-sky-500 to-blue-500',
    color: '#0ea5e9',
  },
]

const storyPoints = [
  {
    icon: Globe,
    title: 'The Problem',
    description: 'Government contracting is fragmented, opaque, and overwhelming. Small and mid-size businesses are locked out by complexity, not capability.',
  },
  {
    icon: Rocket,
    title: 'Our Approach',
    description: 'We built a platform that aggregates every opportunity, deploys AI to surface the best matches, and gives you the tools to compete like a prime contractor.',
  },
  {
    icon: BarChart3,
    title: 'Your Advantage',
    description: 'From opportunity discovery to proposal generation to bid management—everything you need in one platform, powered by proprietary data and advanced AI.',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
}

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto px-4 py-16 md:py-24">

        {/* Hero */}
        <motion.div
          className="text-center mb-14 md:mb-16"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">About GovCon Data</span>
          </motion.div>
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-4 leading-tight"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Level the Playing Field
            </span>
            <br />
            <span className="text-foreground">for Government Contractors</span>
          </motion.h1>
          <motion.p variants={itemVariants} className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We combine advanced AI, proprietary data, and deep industry expertise to help businesses
            of all sizes find, compete for, and win government contracts.
          </motion.p>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-14 md:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          {stats.map((stat) => (
            <motion.div key={stat.label} variants={itemVariants}>
              <div className={cn(
                "text-center p-6 md:p-8 rounded-2xl border transition-all duration-300",
                "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10",
                "hover:shadow-[0_0_30px_-5px_rgba(14,165,233,0.25)] hover:-translate-y-1 hover:border-sky-500/30"
              )} style={{
                boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
              }}>
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent mb-2">
                  <CountingNumber value={stat.number} suffix={stat.suffix} />
                </div>
                <div className="text-sm font-semibold text-foreground mb-1">{stat.label}</div>
                <div className="text-xs text-muted-foreground">{stat.description}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Our Story */}
        <motion.div
          className="mb-14 md:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
              <Rocket className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Our Story</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Why We Built GovCon Data
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {storyPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <motion.div key={point.title} variants={itemVariants}>
                  <div className={cn(
                    "relative h-full rounded-2xl border p-5 md:p-6 transition-all duration-300",
                    "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10",
                    "hover:shadow-[0_0_30px_-5px_rgba(14,165,233,0.25)] hover:-translate-y-1 hover:border-sky-500/30"
                  )} style={{
                    boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
                  }}>
                    <div className="w-1 h-1 absolute top-0 left-0 right-0 rounded-t-2xl bg-gradient-to-r from-sky-400 to-blue-600" style={{ height: '3px' }} />
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-sky-500/70 tracking-widest uppercase">0{index + 1}</span>
                      <div className="h-px flex-1 bg-gradient-to-r from-sky-500/30 to-transparent" />
                    </div>
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 w-fit mb-3">
                      <Icon className="w-5 h-5 text-sky-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{point.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{point.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Mission */}
        <motion.div
          className="mb-14 md:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <motion.div
            variants={itemVariants}
            className={cn(
              "relative rounded-2xl border p-6 md:p-10 text-center overflow-hidden",
              "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
            )}
            style={{
              boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-blue-500/5 pointer-events-none" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Our Mission</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
                Empowering Every Contractor to Compete and Win
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Our mission is to empower businesses of all sizes to navigate and succeed in the government marketplace.
                We leverage cutting-edge AI and data analytics to transform complex procurement processes into clear,
                actionable opportunities—enabling our clients to compete, win, and thrive across local, state, and federal contracting.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Values */}
        <motion.div
          className="mb-14 md:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
              <Award className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">What Drives Us</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Our Values
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <motion.div key={value.title} variants={itemVariants} className="group">
                  <div className={cn(
                    "relative h-full rounded-2xl border p-6 transition-all duration-300",
                    "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10",
                    "hover:shadow-[0_0_30px_-5px_rgba(14,165,233,0.25)] hover:-translate-y-1 hover:border-sky-500/30"
                  )} style={{
                    boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
                  }}>
                    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl bg-gradient-to-r opacity-60 group-hover:opacity-100 transition-opacity" style={{
                      background: `linear-gradient(to right, ${value.color}, ${value.color}88)`,
                    }} />
                    <div className="flex items-center gap-3 mb-4">
                      <div className={cn(
                        "p-2.5 rounded-xl bg-gradient-to-br transition-all duration-300 group-hover:scale-110",
                        value.gradient
                      )}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">{value.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{value.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <motion.div
            variants={itemVariants}
            className={cn(
              "relative rounded-2xl border p-6 md:p-10 overflow-hidden",
              "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
            )}
            style={{
              boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-blue-500/5 pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
                Ready to Elevate Your Strategy?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
                Explore our tailored plans and find the perfect fit to unlock powerful AI insights
                and streamline your government contracting success.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="text-base px-8 h-12 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30">
                  <Link href="/pricing">
                    View Pricing Plans
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-base px-8 h-12 border-sky-500/30 hover:bg-sky-500/5">
                  <Link href="/contact">
                    Contact Sales
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>

      </div>
    </div>
  )
}
